import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import CreateCampaignNameModal from "../../components/Modals/CreateCampaignNameModal";
import { ownerSelector } from "../../store/store";
import { checkExistingName, fetchCampaignsWithMetrics, fetchOwner } from "../../utils";
import axios from "../../utils/axios";
import {
  Button,
  ServerTablePagination,
  Select,
  TableRowActionMenu,
} from "../../components/ui";
import { buildPaginationPayload } from "../../utils/pagination";
import { withActionLock } from "../../hooks/useAsyncAction";
import {
  clearPersistedDraftCampaignId,
  draftSearchFromCampaignId,
  hasLinkedCampaignLists,
  normalizeCampaignListIds,
} from "./campaignDraftFlow";

const DELETABLE_CAMPAIGN_STATUSES = [
  "DRAFT",
  "SCHEDULED",
  "PAUSED",
  "ARCHIVED",
  "COMPLETED",
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "running", label: "Running" },
  { value: "scheduled", label: "Scheduled" },
  { value: "paused", label: "Paused" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

export type CampaignTableRow = {
  key: string;
  status: string;
  campaignStatusRaw?: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  scheduleFromTime?: string;
  scheduleToTime?: string;
  prospectsCount: number;
  listId?: string;
  listIds?: string[];
  performance: {
    leads: number;
    opens: number;
    replies: number;
  };
  progress: {
    queued: number;
    sent: number;
    done: number;
  };
  sender: string;
  senderAlt: string;
  senderName?: string;
  senderImage?: string;
};

const campaignStatusLabel = (status: string) => {
  if (status === "running") return "Active";
  if (status === "paused") return "Paused";
  if (status === "scheduled") return "Scheduled";
  if (status === "draft") return "Draft";
  if (status === "archived") return "Archived";
  if (status === "finished") return "Completed";
  return status;
};

const campaignStatusClass = (status: string) => {
  if (status === "running" || status === "finished") return "bg-success-bg text-success";
  if (status === "paused") return "bg-proxy-warning-bg text-proxy-warning-text";
  return "bg-bg-grey text-muted";
};

export default function CampaignListing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { owner, currentWorkspace } = useSelector(ownerSelector);
  const [status, setStatus] = useState("all");
  const searchQuery = (searchParams.get("q") || "").trim();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(6);
  const [campaignRows, setCampaignRows] = useState<CampaignTableRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const resolveWorkspaceId = useCallback(() => {
    if (typeof currentWorkspace !== "string") return currentWorkspace?._id || undefined;
    try {
      return JSON.parse(currentWorkspace)?._id || undefined;
    } catch {
      return undefined;
    }
  }, [currentWorkspace]);

  const handleCreateCampaignNext = async (campaignName: string) => {
    await withActionLock("campaign-create-name", async () => {
      const ownerId = owner?._id;
      const userId = owner?.userId;
      const workspaceId = resolveWorkspaceId();

      if (!ownerId) {
        toast.warning("Please link your linkedin account first before using campaigns.");
        setIsCreateModalOpen(false);
        navigate("/teams");
        return;
      }

      const isNameAvailable = await checkExistingName(
        campaignName,
        ownerId,
        userId,
        workspaceId
      );

      if (!isNameAvailable) {
        toast.error("Campaign name already exists");
        return;
      }

      clearPersistedDraftCampaignId();
      setIsCreateModalOpen(false);
      navigate("/campaign-management/create", {
        state: {
          campaignName,
          scope: { ownerId, userId, workspaceId },
        },
      });
    });
  };

  const handleChangeRowsPerPage = (nextSize: number) => {
    setRowsPerPage(nextSize);
    setPage(0);
  };

  const backendStatus = useMemo(() => {
    if (!status || status === "all") return undefined;
    if (status === "running") return "ACTIVE";
    if (status === "scheduled") return "SCHEDULED";
    if (status === "paused") return "PAUSED";
    if (status === "draft") return "DRAFT";
    if (status === "archived") return "ARCHIVED";
    return undefined;
  }, [status]);

  const loadCampaigns = useCallback(async () => {
    console.log("loadCampaigns", owner?._id);
    if (!owner?._id) return;
    const workspaceId = resolveWorkspaceId();
    const userId = owner?.userId;

    setLoading(true);
    try {
      const { campaigns, totalCount: apiTotalCount } = await fetchCampaignsWithMetrics(
        owner._id,
        userId,
        workspaceId,
        {
          ...buildPaginationPayload(page, rowsPerPage),
          status: backendStatus,
          search: searchQuery.trim().length ? searchQuery.trim() : undefined,
        }
      );

      const mapStatus = (campaignStatus: string) => {
        if (!campaignStatus) return "draft";
        const normalized = String(campaignStatus).toUpperCase();
        if (normalized === "ACTIVE") return "running";
        if (normalized === "PAUSED") return "paused";
        if (normalized === "SCHEDULED") return "scheduled";
        if (normalized === "DRAFT") return "draft";
        if (normalized === "ARCHIVED") return "archived";
        if (normalized === "COMPLETED") return "finished";
        return normalized.toLowerCase();
      };

      const rows: CampaignTableRow[] = (campaigns || []).map((c: any) => {
        const metrics = c.metrics || {};
        return {
          key: String(c._id),
          status: mapStatus(c.campaignStatus),
          campaignStatusRaw: typeof c.campaignStatus === "string" ? c.campaignStatus : undefined,
          name: c.campaignName || "",
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          scheduleStartDate: c?.schedule?.startDate,
          scheduleEndDate: c?.schedule?.endDate,
          scheduleFromTime: c?.schedule?.from,
          scheduleToTime: c?.schedule?.to,
          prospectsCount: (() => {
            const fromNProspect = typeof c?.nProspect === "number" ? c.nProspect : null;
            const fromProspects = Array.isArray(c?.prospects) ? c.prospects.length : null;
            if (fromProspects !== null) return fromProspects;
            if (fromNProspect !== null) return fromNProspect;
            return fromNProspect ?? 0;
          })(),
          listId:
            typeof c.listId === "string"
              ? c.listId
              : c.listId?._id
                ? String(c.listId._id)
                : undefined,
          listIds: normalizeCampaignListIds(c.listIds, c.listId),
          performance: metrics.performance || { leads: 0, opens: 0, replies: 0 },
          progress: metrics.progress || { queued: 0, sent: 0, done: 0 },
          sender: String(c.ownerId || ""),
          senderAlt: String(c.ownerId || ""),
          senderImage: undefined,
        };
      });

      const ownerIds = Array.from(
        new Set(rows.map((r) => r.sender).filter(Boolean))
      );
      const ownerImagesById: Record<string, string> = {};
      const ownerNamesById: Record<string, string> = {};
      await Promise.all(
        ownerIds.map(async (id) => {
          try {
            const owner = await fetchOwner(id);
            const img =
              owner?.picture || owner?.profilePic || owner?.profileImage || "";
            const name = String(owner?.name || "").trim();
            if (img) ownerImagesById[String(id)] = img;
            if (name) ownerNamesById[String(id)] = name;
          } catch {
            // ignore failures; fallback icon will be shown
          }
        })
      );

      const hydratedRows = rows.map((r) => ({
        ...r,
        senderImage: ownerImagesById[String(r.sender)] || undefined,
        senderName: ownerNamesById[String(r.sender)] || "",
      }));

      setCampaignRows(hydratedRows);
      setTotalCount(apiTotalCount || 0);
    } catch (e) {
      toast.error("Unable to fetch campaigns.");
      setCampaignRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [backendStatus, owner?._id, owner?.userId, resolveWorkspaceId, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const visibleRows = campaignRows;

  return (
    <section className="noScroll h-full overflow-y-auto overflow-x-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6">
          <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">Campaigns</h1>
          <p className="mt-1 text-sm text-muted">
            Build simple sequences that move prospects forward.
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <Select
            variant="compact"
            size="sm"
            ariaLabel="Select status"
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            className="h-10 w-[140px] !border-line font-semibold text-dark-blue"
          />
          <Button
            variant="primary"
            size="sm"
            className="h-10 px-3.5 font-semibold text-dark-blue"
            onClick={() => setIsCreateModalOpen(true)}
          >
            + Create Campaign
          </Button>
        </div>

        {loading && visibleRows.length === 0 ? (
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="min-h-[210px] animate-pulse rounded-[10px] border border-line bg-white p-[17px]"
              />
            ))}
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="rounded-[10px] border border-line bg-white px-6 py-12 text-center">
            <p className="text-sm text-muted">
              {searchQuery ? "No campaigns match your search." : "No campaigns yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
            {visibleRows.map((row) => {
              const contacted = Number(row.progress?.queued || 0);
              const progressPct =
                row.prospectsCount > 0
                  ? Math.min(100, Math.round((contacted / row.prospectsCount) * 100))
                  : 0;
              const senderLine = row.senderName
                ? `${row.senderName} · LinkedIn sender`
                : "";
              return (
                <article
                  key={row.key}
                  className="flex min-h-[210px] flex-col rounded-[10px] border border-line bg-white p-[17px]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`inline-block rounded-xl px-1.5 py-1 text-[10px] font-semibold ${campaignStatusClass(
                        row.status
                      )}`}
                    >
                      {campaignStatusLabel(row.status)}
                    </span>
                    <CampaignRowActions row={row} />
                  </div>
                  <h2 className="mt-4 text-[15px] font-semibold tracking-tight text-dark-blue">
                    {row.name || ""}
                  </h2>
                  <p className="mt-1 text-[11px] text-muted">{senderLine}</p>
                  <div className="mt-5 h-1.5 overflow-hidden rounded bg-paper">
                    <span
                      className="block h-full rounded bg-bright-green"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="mt-3.5 grid grid-cols-3 items-end text-[10px] text-muted">
                    <span>
                      <b className="block min-h-[21px] text-[14px] font-semibold leading-[21px] text-dark-blue">
                        {row.prospectsCount || 0}
                      </b>
                      Prospects
                    </span>
                    <span className="text-center">
                      <b className="block min-h-[21px] text-[14px] font-semibold leading-[21px] text-dark-blue">
                        {contacted || 0}
                      </b>
                      Contacted
                    </span>
                    <span className="text-right">
                      <b className="block min-h-[21px] text-[14px] font-semibold leading-[21px] text-dark-blue">
                        {row.performance?.replies || 0}
                      </b>
                      Replies
                    </span>
                  </div>
                  <Link
                    to={`/campaign-management/view/${row.key}`}
                    state={{ campaign: row }}
                    className="mt-auto pt-4 text-xs font-semibold text-sidebar-hover no-underline hover:underline"
                  >
                    View campaign →
                  </Link>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-4">
          <ServerTablePagination
            page={page}
            pageSize={rowsPerPage}
            total={totalCount}
            rowsPerPageOptions={[6, 12, 24, 48]}
            onPageChange={setPage}
            onPageSizeChange={handleChangeRowsPerPage}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px]">
          <p className="text-sm font-semibold text-dark-blue">One active campaign per LinkedIn account</p>
          <p className="text-xs text-notice-text">
            A sender is unavailable while it is running another active campaign. Existing campaigns are
            never silently replaced.
          </p>
        </div>
      </div>

      <CreateCampaignNameModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onNext={handleCreateCampaignNext}
      />
    </section>
  );
}

function CampaignRowActions({ row }: { row: CampaignTableRow }) {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const statusRaw = (row.campaignStatusRaw || "").toUpperCase();
  const canDelete = DELETABLE_CAMPAIGN_STATUSES.includes(statusRaw);

  const resolveScope = () => {
    try {
      const ownerRaw = localStorage.getItem("owner");
      const parsedOwner = ownerRaw ? JSON.parse(ownerRaw) : null;
      return {
        ownerId: parsedOwner?._id || "",
        userId: parsedOwner?.userId || "",
        workspaceId: (() => {
          try {
            const wsRaw = localStorage.getItem("currentWorkSpace");
            const parsedWs = wsRaw ? JSON.parse(wsRaw) : null;
            return parsedWs?._id || "";
          } catch {
            return "";
          }
        })(),
      };
    } catch {
      return { ownerId: "", userId: "", workspaceId: "" };
    }
  };

  const updateCampaignStatus = async (nextStatus: "ACTIVE" | "PAUSED" | "ARCHIVED") => {
    const { ownerId, userId, workspaceId } = resolveScope();
    if (!ownerId) {
      toast.error("Missing owner context.");
      return;
    }

    await withActionLock(`campaign-status:${row.key}:${nextStatus}`, async () => {
      const loader = toast.loading("Updating campaign...");
      try {
        // Status-only payload: do NOT re-upload prospects/sequence (causes 413 on large campaigns).
        await axios.post("/campaign/editCampaign", {
          ownerId,
          userId: userId || undefined,
          workspaceId: workspaceId || undefined,
          campaignId: row.key,
          campaignStatus: nextStatus,
        });

        toast.update(loader, {
          render: nextStatus === "PAUSED" ? "Campaign paused." : nextStatus === "ACTIVE" ? "Campaign resumed." : "Campaign archived.",
          type: "success",
          isLoading: false,
          autoClose: 2500,
          closeOnClick: true,
        });
        // simplest + safest: reload the page list
        window.location.reload();
      } catch (err: any) {
        const statusCode = Number(err?.response?.status || 0);
        const serverError =
          statusCode === 413
            ? "Request too large. Please refresh and try again."
            : err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Unable to update campaign.";
        toast.update(loader, {
          render: serverError,
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        });
      }
    });
  };

  const syncCampaignList = async () => {
    const { ownerId, userId, workspaceId } = resolveScope();
    if (!ownerId) {
      toast.error("Missing owner context.");
      return;
    }
    if (!hasLinkedCampaignLists(row.listIds, row.listId)) {
      toast.error("This campaign has no linked lists.");
      return;
    }

    await withActionLock(`campaign-sync:${row.key}`, async () => {
      const loader = toast.loading("Syncing lists...");
      try {
        const response = await axios.post("/campaign/syncCampaignList", {
          ownerId,
          userId: userId || undefined,
          workspaceId: workspaceId || undefined,
          campaignId: row.key,
        });
        const addedCount = Number(response?.data?.addedCount || 0);
        const message =
          response?.data?.message ||
          (addedCount > 0
            ? `Added ${addedCount} prospect(s) from linked lists.`
            : "Lists are already in sync.");

        toast.update(loader, {
          render: message,
          type: "success",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        });
        if (addedCount > 0) {
          window.location.reload();
        }
      } catch (err: any) {
        const serverError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to sync list.";
        toast.update(loader, {
          render: serverError,
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        });
      }
    });
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setShowDeleteConfirm(false);
    setDeleteError("");
  };

  const handleDeleteCampaign = async () => {
    const { ownerId, userId, workspaceId } = resolveScope();
    if (!ownerId) {
      toast.error("Missing owner context.");
      return;
    }

    await withActionLock(`campaign-delete:${row.key}`, async () => {
      setIsDeleting(true);
      setDeleteError("");
      try {
        await axios.post("/campaign/deleteCampaign", {
          ownerId,
          userId: userId || undefined,
          workspaceId: workspaceId || undefined,
          campaignId: row.key,
        });
        toast.success("Campaign deleted.");
        setShowDeleteConfirm(false);
        window.location.reload();
      } catch (err: any) {
        const serverError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to delete campaign.";
        setDeleteError(
          typeof serverError === "string" ? serverError : "Unable to delete campaign."
        );
      } finally {
        setIsDeleting(false);
      }
    });
  };

  return (
    <>
    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
    <TableRowActionMenu
      onViewDetails={() => {
        navigate(`/campaign-management/view/${row.key}`, { state: { campaign: row } });
      }}
      onEdit={
        row.status === "draft"
          ? () =>
              navigate(
                { pathname: "/campaign-management/create", search: draftSearchFromCampaignId(row.key) },
                { state: { campaignId: row.key } }
              )
          : undefined
      }
      onPause={
        (row.campaignStatusRaw || "").toUpperCase() === "ACTIVE"
          ? () => void updateCampaignStatus("PAUSED")
          : undefined
      }
      onResume={
        (row.campaignStatusRaw || "").toUpperCase() === "PAUSED"
          ? () => void updateCampaignStatus("ACTIVE")
          : undefined
      }
      onSyncList={() => void syncCampaignList()}
      onArchive={
        ["ACTIVE", "PAUSED", "SCHEDULED"].includes(
          (row.campaignStatusRaw || "").toUpperCase()
        )
          ? () => void updateCampaignStatus("ARCHIVED")
          : undefined
      }
      archiveLabel="Archive"
      archiveTone="success"
      onDelete={canDelete ? () => setShowDeleteConfirm(true) : undefined}
    />
    </div>

    {showDeleteConfirm && typeof document !== "undefined"
      ? createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
        <div className="relative w-full max-w-md min-w-0 rounded-lg bg-white p-6 text-left shadow-xl">
          <button
            type="button"
            aria-label="Close"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            onClick={closeDeleteConfirm}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M1 1l12 12M13 1L1 13" />
            </svg>
          </button>
          <p className="pr-8 text-left text-lg font-semibold">Delete this campaign?</p>
          <p className="mt-3 text-left text-sm text-gray-600">
            <span className="font-medium text-dark-black">{row.name}</span> will be removed from
            your dashboard. Queued steps will be cancelled. Your leads and lists will be kept.
            This cannot be undone.
          </p>
          {deleteError ? <p className="mt-3 text-left text-sm text-red-600">{deleteError}</p> : null}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              isDisabled={isDeleting}
              onClick={closeDeleteConfirm}
            >
              Cancel
            </Button>
            <Button
              variant="btn-red"
              size="sm"
              isDisabled={isDeleting}
              onClick={() => void handleDeleteCampaign()}
            >
              {isDeleting ? "Deleting..." : "Delete campaign"}
            </Button>
          </div>
        </div>
      </div>,
      document.body
    )
      : null}
    </>
  );
}
